import React, { FC } from 'react';

import Button from '@components/Button';
import Input from '@components/Input';

import { useForgotPasswordMutation } from '@features/Auth/services/auth';
import { useTranslation } from 'next-i18next';
import styles from '../CampaignAccordion.module.css';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

const {
  SubmitButton,
  ForgotPassword,
  ForgotPasswordSent,
  InvalidUser,
  InvalidUserText,
} = styles;

interface CampaignLogin {
  formik: any;
  authError: FetchBaseQueryError | undefined;
  toggleForm: () => void;
  resetAuth: () => void;
}
const CampaignLogin: FC<CampaignLogin> = ({
  formik,
  authError,
  toggleForm,
  resetAuth,
}) => {
  const { t } = useTranslation(['common', 'account, campaign']);

  const [
    forgotPassword,
    {
      data: forgotPasswordData,
      isLoading: isLoadingForgotPassword,
      isError: isForgotPasswordError,
      reset: resetForgotPassword,
    },
  ] = useForgotPasswordMutation();

  return (
    <>
      <Input
        name="email"
        type="text"
        label={t('email', { ns: 'common' })}
        placeholder={t('yourEmail', { ns: 'common' })}
        value={formik.values.email}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.errors.email}
        errorMessage={formik.errors.email}
      />
      <Input
        name="password"
        type="password"
        label={t('password', { ns: 'common' })}
        placeholder={t('yourPassword', { ns: 'account' })}
        value={formik.values.password}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.password && formik.errors.password}
        errorMessage={formik.errors.password}
      />
      {authError?.status === 422 && (
        <div className={InvalidUser}>
          <p className={InvalidUserText}>
            {t('wrongEmailOrPassword', { ns: 'campaign' })}
          </p>
        </div>
      )}
      {isForgotPasswordError && (
        <div className={InvalidUser}>
          <p className={InvalidUserText}>Ingen bruger fundet</p>
        </div>
      )}
      <Button
        appearance="primary"
        size="large"
        fullWidth
        type="submit"
        className={SubmitButton}
        hasArrow={false}
        onClick={() => resetForgotPassword()}
      >
        {t('login', { ns: 'common' })}
      </Button>

      <div className={ForgotPassword}>
        <div>
          <Button
            appearance="text"
            size="small"
            type="button"
            hasArrow={false}
            onClick={toggleForm}
          >
            {t('back', { ns: 'common' })}
          </Button>
        </div>
        {forgotPasswordData ? (
          <div className={ForgotPasswordSent}>
            {t('emailSent', { ns: 'common' })}
            <img src="/assets/icons/checkmark-circle-filled.svg" />
          </div>
        ) : (
          <Button
            appearance="text"
            size="small"
            type="button"
            hasArrow={false}
            onClick={() => {
              resetForgotPassword();
              resetAuth();
              forgotPassword({
                email: formik.values.email,
              }).unwrap();
            }}
            disabled={isLoadingForgotPassword}
          >
            {`${t('forgotPassword', { ns: 'common' })}?`}
          </Button>
        )}
      </div>
    </>
  );
};

export default CampaignLogin;
